// Domain types split out of the former monolithic `services/interface.ts` (MDCL-16193).
// Import via the `services/interface` barrel; this module is a domain slice, not a public entry point.
import type { LimitInterval } from 'types/notifications';
import type { ScanResultCode } from 'types/Workflows';

export interface Organization {
    organization_id?: string;
    limit_feed?: number;
    limit_prevention?: number;
    limit_reputation?: number;
    limit_sandbox?: number;
    expiration_date?: string;
    ip_whitelist?: {
        v4: string[];
        v6: string[];
    };
    licenseLimitsTimeInterval?: LimitInterval;
    file_scan_thresholds?: number[];
    scan_result_codes?: ScanResultCode[];
    mdc_license_type?: LicenseType;
    throttling_limit?: number;
    max_upload_file_size?: number;
    max_archive_file_size?: number;
    max_archive_file_number?: number;
    max_archive_file_depth?: number;
    qos_scan?: string;
    sandbox_qos_scan?: string;
    ref_token?: string;
    enforce_private_scan?: boolean;
}

export enum LicenseType {
    Commercial = 'commercial',
    Community = 'community',
    Enterprise = 'enterprise',
    Internal = 'internal',
    Professional = 'professional',
    Standard = 'standard',
}

export interface Apikey {
    apikey?: string;
    throttling_limit?: number;
    max_upload_file_size?: number;
    max_archive_file_size?: number;
    max_archive_file_depth?: number;
    max_archive_file_number?: number;
    max_file_download?: number;
    max_custom_workflows?: number;
    limit_prevention?: number;
    limit_reputation?: number;
    limit_threat_intel_search?: number;
    limit_sandbox?: number;
    limit_feed?: number;
    qos_scan?: string;
    time_interval?: LimitInterval;
    file_scan_thresholds?: number[];
    scan_result_codes?: ScanResultCode[];
    portal_api_key?: string;
    public_key?: string;
    created_at?: string;
    nickname?: string;
    sandbox_qos_scan?: string;
    expiration_date?: string;
    vulnerability_submissions?: {
        fileName?: string;
        hash?: string;
        isCVE?: boolean;
        status?: string;
        created_at?: string;
        updated_at?: string;
    }[];
    is_enterprise?: boolean;
    mtls_only?: boolean;
    is_encrypt_enable?: boolean;
    workflow_rule?: number;
    enforce_private_scan?: boolean;
    blocked_avs?: unknown[];
    partner?: number;
    source?: string;
    sso_user_id?: string;
    userid?: string;
    license_change_note?: string;
    mdc_license_type?: LicenseType;
    updated_at?: string;
    csfs?: {
        userName?: string;
        loginUrl?: string;
        clientKey?: string;
        privateKey?: string;
    };
    organization?: Organization;
    isOnlineStore?: boolean;
    paid_user?: number;
    license?: {
        subscription_id?: string;
        created_at?: string;
        purchase_information?: {
            plan_id?: string;
            amount?: number;
            type?: string;
            tier?: string;
            currency?: string;
            nickname?: string;
            _id?: string;
        }[];
        invoice_url?: string;
        period_end?: string;
        period_start?: string;
        receipt_url?: string;
        canceled_at?: string;
    };
}

export interface User extends Apikey {
    email?: string;
    firstName?: string;
    lastName?: string;
    org_role?: 'user' | 'admin' | 'analyst' | null;
    isLoggedIn?: boolean;
    ipRestriction?: boolean;
    licenseInfo?: {
        paidUser?: number;
        licenseType?: LicenseType;
        expirationDate?: string;
    };
    license?: {
        period_end?: string;
    };
    /**
     * MDCI-1054: retention floor for the stats pages, sent by `/api/variables`. `null` on the
     * legacy upstream (full history); a `YYYY-MM-DD` date on the ClickHouse upstream, set only
     * for tiers it models. Every retention-driven UI behaviour keys off this one value.
     */
    stats?: {
        earliestDate: string | null;
    };
}

export interface Sso extends Partial<User> {
    apikey?: string;
}

export interface CsrfHeader {
    token: string;
    sso: Sso;
}
